import React from 'react';
import styled from 'styled-components';

interface BottomNavigationProps {
  currentPage: string;
  onNavigate: (page: 'alarm' | 'assignments' | 'points-table' | 'chat' | 'board') => void;
}

const BottomNavContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border-top: 1px solid var(--color-border);
  padding: 2px 0;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  min-height: 32px;
  box-shadow: 0 -1px 5px rgba(0, 0, 0, 0.03);
  z-index: 1000;
  
  /* 그라데이션 효과 */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, var(--color-border), transparent);
  }
  
  @media (max-width: 480px) {
    gap: 12px;
    padding: 2px 8px;
  }
  
  @media (max-width: 360px) {
    gap: 8px;
    padding: 2px 4px;
  }
`;

const NavButton = styled.button<{ $isActive: boolean }>`
  background: none;
  border: none;
  color: ${props => props.$isActive ? 'var(--color-accent-strong)' : 'var(--color-text-muted)'};
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  transition: all 0.2s ease;
  min-width: 50px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  
  &:hover {
    background: rgba(255, 255, 255, 0.6);
    color: var(--color-accent-text);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  @media (max-width: 768px) {
    padding: 6px 10px;
    min-width: 60px;
  }
  
  @media (max-width: 480px) {
    padding: 4px 6px;
    min-width: 50px;
  }
  
  @media (max-width: 360px) {
    padding: 4px 4px;
    min-width: 44px;
  }
`;

const NavIcon = styled.div<{ $isActive: boolean }>`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    width: 100%;
    height: 100%;
    filter: ${props => props.$isActive 
      ? 'brightness(0) saturate(100%) invert(25%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(0.4) contrast(1)' 
      : 'brightness(0) saturate(100%) invert(75%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(0.9) contrast(0.5)'
    };
    transition: filter 0.2s ease;
  }
  
  @media (max-width: 768px) {
    width: 22px;
    height: 22px;
  }
  
  @media (max-width: 480px) {
    width: 20px;
    height: 20px;
  }
  
  @media (max-width: 360px) {
    width: 18px;
    height: 18px;
  }
`;

const NavLabel = styled.span<{ $isActive: boolean }>`
  font-size: 9px;
  font-weight: ${props => props.$isActive ? '600' : '400'};
  color: ${props => props.$isActive ? 'var(--color-accent-strong)' : 'var(--color-text-muted)'};
  transition: all 0.2s ease;
  
  @media (max-width: 768px) {
    font-size: 10px;
  }
  
  @media (max-width: 480px) {
    font-size: 9px;
  }
  
  @media (max-width: 360px) {
    font-size: 8px;
  }
`;

const BottomNavigation: React.FC<BottomNavigationProps> = ({ currentPage, onNavigate }) => {
  return (
    <BottomNavContainer>
      <NavButton 
        $isActive={currentPage === 'alarm'}
        onClick={() => onNavigate('alarm')}
        tabIndex={-1}
      >
        <NavIcon $isActive={currentPage === 'alarm'}>
          <img src="/alram.svg" alt="알람" />
        </NavIcon>
        <NavLabel $isActive={currentPage === 'alarm'}>알람</NavLabel>
      </NavButton>
      <NavButton 
        $isActive={currentPage === 'assignments'}
        onClick={() => onNavigate('assignments')}
        tabIndex={-1}
      >
        <NavIcon $isActive={currentPage === 'assignments'}>
          <img src="/task.svg" alt="과제" />
        </NavIcon>
        <NavLabel $isActive={currentPage === 'assignments'}>과제</NavLabel>
      </NavButton>
      <NavButton 
        $isActive={currentPage === 'points-table'}
        onClick={() => onNavigate('points-table')}
        tabIndex={-1}
      >
        <NavIcon $isActive={currentPage === 'points-table'}>
          <img src="/point.svg" alt="포인트" />
        </NavIcon>
        <NavLabel $isActive={currentPage === 'points-table'}>포인트</NavLabel>
      </NavButton>
      <NavButton 
        $isActive={currentPage === 'chat'}
        onClick={() => onNavigate('chat')}
        tabIndex={-1}
      >
        <NavIcon $isActive={currentPage === 'chat'}>
          <img src="/Chat.svg" alt="챗" />
        </NavIcon>
        <NavLabel $isActive={currentPage === 'chat'}>챗</NavLabel>
      </NavButton>
      <NavButton 
        $isActive={currentPage === 'board'}
        onClick={() => onNavigate('board')}
        tabIndex={-1}
      >
        <NavIcon $isActive={currentPage === 'board'}>
          <img src="/blackboard.svg" alt="보드" />
        </NavIcon>
        <NavLabel $isActive={currentPage === 'board'}>보드</NavLabel>
      </NavButton>
    </BottomNavContainer>
  );
};

export default BottomNavigation;
